import { create } from 'zustand';
import { ListCouncils, CreateCouncil, GetCouncil, UpdateCouncil, DeleteCouncil } from '../api/api';
import toast from 'react-hot-toast';

export interface CouncilMember {
  _id: string;
  name: string;
  dob?: string;
  education?: string;
  job?: string;
  contactNumbers?: string[];
}

export interface CouncilMemberWithPosition {
  member?: string | CouncilMember;
  name?: string;
  position?: string;
}

export interface Council {
  _id: string;
  period: string;
  members: Array<string | CouncilMember | CouncilMemberWithPosition>;
  description?: string;
  image?: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateCouncilI {
  period: string;
  members: Array<{ member?: string; name?: string; position?: string }>;
  description?: string;
  image?: string;
}

interface UpdateCouncilI {
  period?: string;
  members?: Array<{ member?: string; name?: string; position?: string }>;
  description?: string;
  image?: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface GetCouncilsResponse {
  data: {
    councils: Council[];
    pagination?: PaginationInfo;
  };
}

interface CouncilResponse {
  data: {
    council: Council;
  };
  status: boolean;
  message: string;
}

interface DeleteCouncilResponse {
  status: boolean;
  message: string;
}

interface CouncilStore {
  councils: Council[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  getCouncils: (isPublic?: boolean, search?: string, page?: number, limit?: number) => Promise<void>;
  createCouncil: (council: CreateCouncilI) => Promise<void>;
  getCouncil: (id: string) => Promise<Council | null>;
  updateCouncil: (id: string, council: UpdateCouncilI) => Promise<void>;
  deleteCouncil: (id: string) => Promise<void>;
}

export const useCouncilStore = create<CouncilStore>((set, get) => ({
  councils: [] as Council[],
  loading: false,
  error: null,
  pagination: null,

  getCouncils: async (isPublic?: boolean, search?: string, page?: number, limit?: number) => {
    set({ loading: true, error: null });
    const { data, error } = await ListCouncils<GetCouncilsResponse>(isPublic, search, page, limit);

    if (error) {
      set({ loading: false, error: error as string });
      return;
    }

    set({
      councils: data.data.councils || [],
      pagination: data.data.pagination || null,
      loading: false,
    });
  },

  createCouncil: async (council: CreateCouncilI) => {
    set({ loading: true, error: null });
    const { data, error } = await CreateCouncil<CouncilResponse>(council);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to create council' });
      return;
    }

    const councils = get().councils;
    set({ councils: [...councils, data.data.council], loading: false });
    toast.success('Council created successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getCouncils(undefined, undefined, currentPagination?.page, currentPagination?.limit);
  },

  getCouncil: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await GetCouncil<CouncilResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to get council' });
      return null;
    }

    set({ loading: false });
    return data.data.council;
  },

  updateCouncil: async (id: string, council: UpdateCouncilI) => {
    set({ loading: true, error: null });
    const { data, error } = await UpdateCouncil<CouncilResponse>(id, council);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to update council' });
      return;
    }

    const councils = get().councils;
    const updatedCouncils = councils.map((c) => (c._id === id ? { ...c, ...data.data.council } : c));
    set({ councils: updatedCouncils, loading: false });
    toast.success('Council updated successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getCouncils(undefined, undefined, currentPagination?.page, currentPagination?.limit);
  },

  deleteCouncil: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await DeleteCouncil<DeleteCouncilResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to delete council' });
      return;
    }

    const councils = get().councils.filter((council) => council._id !== id);
    set({ councils, loading: false });
    toast.success('Council deleted successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getCouncils(undefined, undefined, currentPagination?.page, currentPagination?.limit);
  },
}));

export type { CreateCouncilI, UpdateCouncilI };
